<script setup>
import { ref, computed, onMounted } from "vue";
import { useI18n } from "vue-i18n";
import Section from "@/widgets/profile-drawer/ui/internal/Section.vue";
import ReadonlyField from "@/widgets/profile-drawer/ui/fields/ReadonlyField.vue";
import { Spinner } from "@/shared/ui/spinner";
import { getPermissionGroupedList } from "@/entities/role/api/permissions";

const props = defineProps({
  data: { type: Object, required: true },
});

const { locale } = useI18n();

const groupedRaw = ref({});
const loading = ref(false);

const userPermissionCodes = computed(() => {
  const list = Array.isArray(props.data.permissions)
    ? props.data.permissions
    : [];
  return new Set(
    list.map((p) => (typeof p === "string" ? p : p?.code)).filter(Boolean)
  );
});

const groupedSections = computed(() => {
  const map = groupedRaw.value || {};
  const sections = [];

  for (const [serviceCode, entries] of Object.entries(map)) {
    const items = Object.values(entries || {})
      .filter((p) => userPermissionCodes.value.has(p.code))
      .map((p) => ({
        code: p.code,
        name: locale.value === "kk" && p.name_kz ? p.name_kz : p.name || p.code,
      }));

    if (items.length) {
      sections.push({ service: serviceCode, items });
    }
  }

  return sections;
});

const knownCodes = computed(() => {
  const set = new Set();
  for (const entries of Object.values(groupedRaw.value || {})) {
    for (const p of Object.values(entries || {})) {
      if (p?.code) set.add(p.code);
    }
  }
  return set;
});

const unknownCodes = computed(() =>
  Array.from(userPermissionCodes.value).filter((c) => !knownCodes.value.has(c))
);

onMounted(async () => {
  loading.value = true;
  try {
    const res = await getPermissionGroupedList();
    const payload = res?.payload ?? res ?? {};
    groupedRaw.value = payload && typeof payload === "object" ? payload : {};
  } catch (e) {
    console.error(e);
  } finally {
    loading.value = false;
  }
});
</script>

<template>
  <div class="flex flex-col">
    <Section
      icon="sli-system"
      :title="$t('Системная информация')"
      :sub="$t('Идентификаторы и тип учётной записи')"
    >
      <div class="grid grid-cols-2 gap-x-5 gap-y-3.5 max-sm:grid-cols-1">
        <ReadonlyField label="UUID" :value="data.uuid" />
        <ReadonlyField :label="$t('ИИН')" :value="data.identifier" />
        <ReadonlyField
          :label="$t('Сотрудник')"
          :value="data.employee ? $t('Да') : $t('Нет')"
        />
      </div>
    </Section>

    <Section
      icon="sli-medal"
      :title="$t('Разрешения')"
      :sub="$t('Эффективные разрешения учётной записи')"
    >
      <div
        v-if="loading"
        class="flex items-center gap-2 text-sm text-muted-foreground"
      >
        <Spinner size="sm" class="text-current" />
        {{ $t("Загрузка разрешений…") }}
      </div>

      <div
        v-else-if="!userPermissionCodes.size"
        class="text-sm text-muted-foreground"
      >
        {{ $t("Нет разрешений") }}
      </div>

      <div v-else class="flex flex-col gap-4">
        <div
          v-for="section in groupedSections"
          :key="section.service"
          class="flex flex-col gap-2"
        >
          <div class="text-[12.5px] font-medium text-muted-foreground">
            {{ section.service }}
          </div>
          <div class="flex flex-wrap gap-2">
            <div
              v-for="p in section.items"
              :key="p.code"
              class="inline-flex items-center gap-2 px-2.5 py-1 rounded-md border border-input bg-background text-[12.5px]"
            >
              <div class="text-muted-foreground">{{ p.code }}</div>
              <div class="text-foreground">{{ p.name }}</div>
            </div>
          </div>
        </div>

        <div v-if="unknownCodes.length" class="flex flex-col gap-2">
          <div class="text-[12.5px] font-medium text-muted-foreground">
            {{ $t("Прочие") }}
          </div>
          <div class="flex flex-wrap gap-2">
            <div
              v-for="code in unknownCodes"
              :key="code"
              class="inline-flex items-center px-2 py-0.5 rounded-md border border-input bg-background text-[12.5px]"
            >
              {{ code }}
            </div>
          </div>
        </div>
      </div>
    </Section>
  </div>
</template>
